import type { Site } from '@/lib/types';
import type {
  OfficeProgressWeekRow,
  OfficeSettingsPayload,
  OfficeSheetResponse,
  OfficeTradePayload,
} from '@/pages/sites/pmis/office/queries';

/**
 * 공무 시트의 값·설정·계산 — <b>화면 초안</b>(DOC-37).
 *
 * <p>모양은 현장이 쓰던 엑셀(`docs/samples/공무탭초안.xlsx`)을 그대로 따른다. 그래서 <b>행은 고정</b>이다 —
 * 현장이 항목을 만드는 표가 아니라, 정해진 지표 열여섯 줄을 매달 채우는 표다.
 *
 * <p>사람이 손으로 치는 칸은 <b>네 개뿐</b>이다(사용자 확정 2026-08-11).
 * <ol>
 *   <li>실적공정률(당월)</li>
 *   <li>투입금액(당월)</li>
 *   <li>세금계산서 발행(당월) — PDF 증빙이 있어야 확정된다</li>
 *   <li>실수금(당월)</li>
 * </ol>
 *
 * <p><b>목표공정률은 직접 고치지 못한다</b>(사용자 확정). 보할 공정표에서 통째로 들여오고, 그 뒤로는
 * 읽기 전용이다 — 목표를 손으로 만질 수 있으면 달성률이 언제나 100% 가 된다. 서버가 서면
 * `pmis_wbs_planned_curve`(공정율 탭의 계획 곡선)에서 읽어 스냅샷하고, 지금은 붙여넣기로 들여온다.
 *
 * <p>나머지 열한 줄은 <b>전부 산식</b>이다. 매출은 공정률 × 도급계약금액으로 나오므로 아무도 매출액을
 * 직접 치지 않는다 — 치게 두면 공정률과 매출이 따로 놀고, 그 순간 이 표는 대사(對査)에 못 쓴다.
 *
 * <p>금액은 <b>원(KRW) 정수</b>로 들고 화면에서만 억원으로 접는다. 억원을 그대로 저장하면 소수점이
 * 남아 합계가 1원씩 어긋난다.
 */

// ── 설정 (샘플 B2:G17) ──────────────────────────────────────────────────────

export type TradeAmount = {
  /** 서버에 저장된 행이면 그 id. 새로 더한 공종은 없다(저장할 때 만들어진다) */
  id?: string;
  /** 공종 — 건축·전기·통신·소방… 현장이 더할 수 있다 */
  trade: string;
  /** VAT 포함 금액(원) */
  withVat: number | null;
  /** VAT 제외 금액(원) — 목표·실적 매출의 분모다 */
  withoutVat: number | null;
};

/**
 * 수금예정일의 기산점 (V268). 라벨은 {@link COLLECT_BASE_LABEL} 에 있다 — 수금관리 탭과 설정 카드가
 * <b>같은 말</b>을 써야 두 화면이 같은 값을 가리키는 것이 보인다.
 */
export type CollectBase = 'CLAIM_DATE' | 'CLAIM_MONTH_END' | 'INVOICE_DATE';

export const COLLECT_BASE_LABEL: Record<CollectBase, string> = {
  CLAIM_DATE: '청구일',
  CLAIM_MONTH_END: '청구월 말일',
  INVOICE_DATE: '세금계산서 발행일',
};

/**
 * 계약 수금조건이 말하는 <b>수금예정일</b> — 기산점 + 일수 (V268 · 사용자 결정 2026-09-14).
 *
 * <p>기산점이 될 날짜가 아직 없으면(청구 전, 발행 전) `null` 이다. <b>짐작해 앉히지 않는다</b> —
 * 오늘 날짜로 채워 두면 아직 청구도 안 한 회차가 현금흐름 곡선에 돈으로 서고, 그 곡선을 보고
 * 자금을 잡는 사람이 없는 돈을 센다.
 *
 * <p>저장하지 않는다. 계약 조건이 바뀌면 지난 회차의 예정일까지 함께 움직여야 하는데, 담아 두면
 * 표와 저장값이 갈라진다(본사관리비를 요율로만 들고 있는 것과 같은 판단).
 */
export function expectedCollectDate(
  base: CollectBase | null,
  offsetDays: number | null,
  claim: { claimDate: string | null; invoiceDate: string | null },
): string | null {
  if (!base || offsetDays == null) return null;

  const anchor =
    base === 'INVOICE_DATE' ? claim.invoiceDate
    : base === 'CLAIM_DATE' ? claim.claimDate
    : monthEndOf(claim.claimDate);
  if (!anchor) return null;

  return addDays(anchor, offsetDays);
}

/** `YYYY-MM-DD` 그 달의 말일. 2월·31일 없는 달을 달력에 맡긴다 — 다음 달 0일이 이번 달 말일이다. */
function monthEndOf(date: string | null): string | null {
  if (!date || date.length < 7) return null;
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  if (!Number.isInteger(year) || !Number.isInteger(month)) return null;
  return iso(new Date(Date.UTC(year, month, 0)));
}

function addDays(date: string, days: number): string | null {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return iso(parsed);
}

/** UTC 로만 다룬다 — 현지 시간으로 찍으면 자정 근처에서 하루가 밀린다. */
function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}


export type OfficeSettings = {
  /** 공사기간 — 표의 월 열이 여기서 나온다(앞뒤로 monthPadBefore/After 개월씩 넓힌다) */
  startDate: string | null;
  endDate: string | null;
  targetCompletionDate: string | null;
  /** 착공 몇 개월 전부터 · 준공 몇 개월 후까지 표에 여는가 (V616 · 0~12, 기본 3) */
  monthPadBefore: number;
  monthPadAfter: number;
  trades: TradeAmount[];
  /** 목표원가율 % */
  targetCostRate: number | null;
  /**
   * 본사관리비 요율 % (V167 · 요청 #35). 당월 본사관리비 = 당월 투입금액 × 이 값.
   *
   * <p>다른 설정과 달리 <b>확정 뒤에도 고친다</b>. 도급계약이 정한 값이 아니라 회사가 자기 관리비를
   * 현장에 얼마로 실을지 정한 값이라 설계변경의 대상이 아니다 — 대신 저장 경로가 따로이고
   * (`PUT …/settings/overhead-rate`) 확정 권한을 요구한다.
   */
  overheadRate: number | null;
  /** 수금비율 % — 선금·기성금·준공유보금. 셋의 합이 100 이어야 한다 */
  advanceRate: number | null;
  progressRate: number | null;
  retentionRate: number | null;
  /** 기성조건 — 매 N개월 청구 */
  claimCycleMonths: number | null;
  /** 수금조건 — 청구월 말일 등. 계약서에 적힌 말 그대로다 */
  collectTerm: string;
  /**
   * 수금예정일의 기산점 (V268) — 수금관리 탭이 이 둘로 회차별 예정일을 낸다.
   *
   * <p>{@link collectTerm} 을 파싱하지 않는 이유: "청구월 말일 익익월" 같은 표현 하나에
   * 현금흐름 곡선이 통째로 틀어지고, 틀어졌다는 것을 아무도 모른다. 둘 중 하나라도 비면
   * 예정일을 만들지 않는다.
   */
  collectBase: CollectBase | null;
  collectOffsetDays: number | null;
  /**
   * 개시 잔액(누계 이월) — <b>추가 제안</b>. 이미 진행 중인 현장을 이 표로 옮길 때 첫 달 누계가
   * 0 이면 수금률·원가율이 전부 틀린다. 샘플에는 없던 칸이다.
   */
  openingPlanProgress: number | null;
  openingActualProgress: number | null;
  openingCost: number | null;
  openingInvoice: number | null;
  openingCollect: number | null;
  /** DRAFT 작성중 · CONFIRMED 확정(잠금) */
  status: 'DRAFT' | 'CONFIRMED';
  confirmedAt: string | null;
  /**
   * 공사금액이 잠긴 시각 (V248). <b>확정과 다른 잠금</b>이다 — 현장 수정 모달의 공사금액 카드가
   * [저장]과 함께 건다. 서버가 확정된 설정에도 이 값을 실어 주므로 화면은 한 칸만 본다.
   */
  contractLockedAt: string | null;
  /** 설계변경 이력 — 잠금을 푼 사실과 그때의 계약금액을 남긴다(§4.6) */
  changes: ContractChange[];
  /** 서버에 설정 행이 있는가. 없으면 아직 아무도 만들지 않은 현장이다 */
  persisted?: boolean;
  /** 낙관적 잠금·표시용. 서버가 준 마지막 저장 시각 */
  updatedAt?: string | null;
};

/**
 * 설계변경 한 건 — <b>잠금을 푸는 유일한 길</b>(사용자 확정 2026-08-11).
 *
 * <p>확정된 설정은 사유와 증빙 없이 열리지 않는다. 그리고 <b>덮어쓰지 않고 남긴다</b> — 계약금액이
 * 바뀌면 과거 달의 목표매출까지 전부 달라지므로, 무엇이 언제 얼마에서 얼마로 바뀌었는지가
 * 표 어딘가에 남아 있어야 지난 보고서를 설명할 수 있다.
 */
export type ContractChange = {
  at: string;
  reason: string;
  /** 증빙(설계변경 계약서 등). 초안이라 파일명만 든다 */
  fileName: string | null;
  /** 변경 <b>직전</b>의 계약금액 스냅샷 */
  beforeWithoutVat: number | null;
  beforeWithVat: number | null;
};

/**
 * 처음 여는 현장에 세워 두는 공종 (사용자 확정 2026-09-10 — 넷에서 일곱으로).
 *
 * <p><b>기본값일 뿐</b>이고 현장이 더하고 뺀다. 순서는 도급내역서가 서는 순서다 —
 * 토목·기계·조경이 빠져 있어 그 금액을 넣을 사람이 매번 [공종 편집]부터 열어야 했다.
 */
export const DEFAULT_TRADES = ['건축', '토목', '기계', '조경', '전기', '통신', '소방'];

// ── 월별 입력 (샘플 K20:W46 중 사람이 채우는 칸) ────────────────────────────

export type MonthInput = {
  /** 목표공정률 당월 % */
  planProgressRate: number | null;
  /** 실적공정률 당월 % — <b>사람이 치는 칸</b>(사용자 확정 2026-08-11) */
  actualProgressRate: number | null;
  /**
   * 그 값이 어디서 왔나. `LEDGER` 면 주간회의에서 넘어온 값이고, 표에서 직접 치면 `MANUAL` 이 되어
   * 이후 회의가 덮지 않는다. 칸을 비우면 다시 `LEDGER` 로 돌아간다.
   */
  actualSource: 'LEDGER' | 'MANUAL';
  /**
   * 투입금액 당월 (원) — 보통은 <b>내역(공종·비목)의 합</b>이다. 표에서 합계를 직접 치면 `costSource`
   * 가 `MANUAL` 이 되어 그 값을 쓴다(V580). 합계를 따로 드는 이유는 산식·대시보드가 매달 이 값을 더하기 때문이다.
   */
  costAmount: number | null;
  /** `DETAIL` 이면 내역의 합 · `MANUAL` 이면 표에서 직접 친 합계. 비우면 다시 `DETAIL` (V580) */
  costSource: 'DETAIL' | 'MANUAL';
  /**
   * <b>공종 × 비목</b> → 그 달 투입금액 (V180 · 요청 No.35 「(외주 + 자재 + 경비) 작성탭 반영」).
   *
   * <p>키는 {@link costCellKey} 가 만든다 — 공종 하나에 외주·자재·경비가 함께 서기 때문이다.
   * 공종은 `pmis_office_contract_amounts.trade` 와 같은 축이고, <b>빈 공종</b>은 「공종 없음」
   * (현장 운영경비처럼 어느 공종에도 안 붙는 돈)이다.
   */
  costByTrade: Record<string, number | null>;
  /**
   * 본사관리비 당월 (원) — <b>덮어쓴 달에만 값이 있다</b>(V167).
   *
   * <p>`overheadSource` 가 `RATE` 면 이 칸은 비어 있고 표가 요율로 계산한다. 계산 결과를 여기 담지
   * 않는 이유: 요율이 바뀌면 지난달까지 함께 움직여야 하는데, 담아 두면 표와 저장값이 갈라진다.
   */
  overheadAmount: number | null;
  /**
   * 본사관리비가 어디서 왔나. `RATE` 면 설정의 요율을 따라가고, 표에서 직접 치면 `MANUAL` 이 되어
   * 그 달만 요율을 따르지 않는다. 칸을 비우면 다시 `RATE` 로 돌아간다 —
   * 실적공정률·실수금과 같은 규약이고, <b>돌아갈 자리가 다른 표가 아니라 설정</b>인 것만 다르다.
   */
  overheadSource: 'RATE' | 'MANUAL';
  /** 세금계산서 발행 당월 (원, VAT 포함) */
  invoiceAmount: number | null;
  /** 실수금 당월 (원) */
  collectAmount: number | null;
  /**
   * 그 값이 어디서 왔나(V99). `LEDGER` 면 기성 탭에서 「완료」로 바뀐 도급기성의 합이고, 표에서
   * 직접 치면 `MANUAL` 이 되어 이후 기성이 덮지 않는다. 비우면 다시 `LEDGER` 로 돌아간다.
   */
  /**
   * 발행액이 어디서 왔나 (V277). `LEDGER` 면 수금관리 회차 가운데 <b>발행일이 그 달인</b> 도급기성의
   * 발행금액 합이고, 표에서 직접 치면 `MANUAL` 이 되어 회차가 덮지 않는다. 비우면 다시 `LEDGER`.
   */
  invoiceSource: 'LEDGER' | 'MANUAL';
  collectSource: 'LEDGER' | 'MANUAL';
  /** 세금계산서 PDF — 초안이라 파일명만 든다. 서버가 서면 문서 id·S3 키가 된다 */
  invoiceFileName: string | null;
  note: string;
  /** OPEN 입력 가능 · CONFIRMED 마감(수정 불가) */
  status: 'OPEN' | 'CONFIRMED';
  confirmedAt: string | null;
};

export const EMPTY_MONTH: MonthInput = {
  planProgressRate: null,
  actualProgressRate: null,
  actualSource: 'LEDGER',
  costAmount: null,
  costSource: 'DETAIL',
  costByTrade: {},
  overheadAmount: null,
  overheadSource: 'RATE',
  invoiceAmount: null,
  collectAmount: null,
  invoiceSource: 'LEDGER',
  collectSource: 'LEDGER',
  invoiceFileName: null,
  note: '',
  status: 'OPEN',
  confirmedAt: null,
};

export type OfficeDraft = {
  settings: OfficeSettings;
  /** key = YYYY-MM */
  months: Record<string, MonthInput>;
  /**
   * 실적공정률의 원천 — 주별 원장(DOC-38). 월값은 이 줄들의 합이라 표에서 직접 못 고친다.
   * 서버가 없는 화면(테스트)에서는 비어 있을 수 있다.
   */
  progressWeeks?: OfficeProgressWeekRow[];
};

// ── 행 정의 (샘플 B20:E46) ──────────────────────────────────────────────────

export type RowGroup = '매출' | '원가' | '원가율' | '수금';
export type RowScope = '당월' | '누계' | '전체';
export type RowFormat = 'percent' | 'money' | 'ratio';

export type RowKey =
  | 'planProgress'
  | 'actualProgress'
  | 'planRevenue'
  | 'actualRevenue'
  | 'achieveRate'
  | 'planCost'
  | 'costInput'
  | 'costSubcontract'
  | 'costMaterial'
  | 'costExpense'
  | 'overhead'
  | 'costTotal'
  | 'costDiffPlan'
  | 'costDiffContract'
  | 'targetCostRate'
  | 'actualCostRate'
  | 'costRateDiff'
  | 'invoice'
  | 'collect'
  | 'receivable'
  | 'collectRate';

export type RowDef = {
  key: RowKey;
  group: RowGroup;
  label: string;
  format: RowFormat;
  scopes: RowScope[];
  /** 사람이 넣는 행이면 그 입력 칸의 이름. 없으면 산식 행이다 */
  input?: keyof Pick<
    MonthInput,
    | 'planProgressRate'
    | 'actualProgressRate'
    | 'costAmount'
    | 'overheadAmount'
    | 'invoiceAmount'
    | 'collectAmount'
  >;
  /**
   * 이 줄이 <b>어느 줄을 쪼갠 것인지</b>. 값이 있으면 그 줄 아래에 접히는 자식이 된다.
   *
   * <p>전에는 라벨 글자에 <b>전각 공백(\u3000)과 가지(├)</b>를 박아 넣고
   * 「샘플에 없던 행」 표시로 {@code +} 를 붙였다. 그 전각 공백은 <b>이름으로</b> 적는다 —
   * 글자 그대로 두면 눈에 안 보이는 데다 eslint(`no-irregular-whitespace`)가 CI 를 세운다.
   * 둘 다 <b>그림으로 흉내 낸 나무</b>였다 — 접을 수 없으니 접고 싶은 사람은 방법이 없고,
   * {@code +} 는 눌러도 아무 일이 없어 무엇을 여는 단추처럼만 보였다(사용자 지적 2026-09-15).
   * 이제 나무는 <b>진짜로</b> 접힌다: 부모 줄의 ▸ 를 누르면 자식 줄이 서고 눕는다.
   */
  parent?: RowKey;
  /**
   * 칸 옆에 <b>내역 창</b>을 여는 단추가 붙는 줄(투입금액). 칸 자체도 직접 친다(2026-10-27) — 친 합계는
   * 그 달의 `MANUAL` 합계가 되어 내역의 합을 따르지 않고, 비우면 다시 내역의 합이 된다(V580).
   */
  drawer?: boolean;
  /**
   * 칸 옆에 <b>주별 창</b>을 여는 단추가 붙는 줄(실적공정률). 칸 자체는 직접 친다 —
   * 창은 주간회의에서 넘어온 값을 보고 주 단위로 나눠 넣는 자리다.
   */
  weeks?: boolean;
  /**
   * 표에 그리지 않는다. <b>계산은 그대로 돌린다</b> — 요구사항이 정해지면 이 표시만 지우면 되고,
   * 지금 지웠다가 나중에 되살리면 그 사이 달들의 값이 비어 버린다.
   */
  hidden?: boolean;
};

export const ROWS: RowDef[] = [
  {
    key: 'planProgress',
    group: '매출',
    label: '목표공정률 (%)',
    format: 'percent',
    scopes: ['당월', '누계'],
    // 입력 칸이 아니다 — 보할 공정표에서 들여온다(사용자 확정 2026-08-11).
  },
  {
    key: 'actualProgress',
    group: '매출',
    label: '실적공정률 (%)',
    format: 'percent',
    scopes: ['당월', '누계'],
    // <b>사람이 치는 칸</b>이다(사용자 확정 2026-08-11). 옆의 작은 단추로 주별 창을 열면
    // 주간회의에서 넘어온 값을 보거나 주 단위로 나눠 넣을 수 있다.
    input: 'actualProgressRate',
    weeks: true,
  },
  {
    key: 'planRevenue',
    group: '매출',
    label: '목표매출액',
    format: 'money',
    scopes: ['당월', '누계'],
  },
  {
    key: 'actualRevenue',
    group: '매출',
    label: '실적매출액',
    format: 'money',
    scopes: ['당월', '누계'],
  },
  {
    key: 'achieveRate',
    group: '매출',
    label: '매출달성률 (%)',
    format: 'percent',
    scopes: ['당월', '누계'],
  },
  {
    key: 'planCost',
    group: '원가',
    label: '목표금액',
    format: 'money',
    scopes: ['당월', '누계'],
  },
  {
    key: 'costInput',
    group: '원가',
    label: '투입금액',
    format: 'money',
    scopes: ['당월', '누계'],
    input: 'costAmount',
    drawer: true,
  },
  /*
    비목 소계 셋 (V180 · 요청 No.35 「(외주 + 자재 + 경비) 작성탭 반영」).

    투입금액의 <b>자식</b>이다({@code parent}) — 이 셋은 위 칸을 쪼갠 것이지 새로운 값이 아니라,
    부모 줄에서 접고 펼 수 있다. 「미분류」 줄은 <b>세우지 않는다</b>: 비어 있는 것이 정상인 줄을
    늘 세우면 표가 한 줄 길어지기만 한다. 미분류 금액은 투입금액과 셋의 합이 어긋나는 것으로
    드러나고, 창을 열면 그 자리에서 보인다.
  */
  {
    key: 'costSubcontract',
    group: '원가',
    label: '외주',
    format: 'money',
    scopes: ['당월', '누계'],
    parent: 'costInput',
  },
  {
    key: 'costMaterial',
    group: '원가',
    label: '자재',
    format: 'money',
    scopes: ['당월', '누계'],
    parent: 'costInput',
  },
  {
    key: 'costExpense',
    group: '원가',
    label: '경비',
    format: 'money',
    scopes: ['당월', '누계'],
    parent: 'costInput',
  },
  {
    // 본사관리비(V167 · 요청 #35 「하부 본사관리비 요율적용 칸 추가 … 수기입력 가능토록」).
    //
    // 투입금액 <b>바로 아래</b>다 — 요청서가 자리까지 말하고 있고, 실제로 이 줄은 위 칸의 함수다.
    // 칸을 직접 칠 수 있게 두는 것은 요청서가 함께 못박은 것이다. 그때 그 달만 요율에서 풀린다.
    key: 'overhead',
    group: '원가',
    label: '본사관리비',
    format: 'money',
    scopes: ['당월', '누계'],
    input: 'overheadAmount',
  },
  {
    key: 'costTotal',
    group: '원가',
    label: '총원가 (투입+본사관리비)',
    format: 'money',
    scopes: ['당월', '누계'],
  },
  {
    key: 'costDiffPlan',
    group: '원가',
    label: '원가 비교 (목표−총원가)',
    format: 'money',
    scopes: ['당월', '누계'],
  },
  {
    key: 'costDiffContract',
    group: '원가',
    label: '원가 비교 (실적매출−총원가)',
    format: 'money',
    scopes: ['당월', '누계'],
  },
  {
    key: 'targetCostRate',
    group: '원가율',
    label: '목표원가율 (%)',
    format: 'percent',
    scopes: ['전체'],
  },
  {
    key: 'actualCostRate',
    group: '원가율',
    label: '실적원가율 (%)',
    format: 'percent',
    scopes: ['당월', '누계'],
  },
  {
    key: 'costRateDiff',
    group: '원가율',
    label: '원가율 비교',
    // % 가 아니라 소수로 읽는다(사용자 확정 2026-08-11) — 10%p 차이는 0.10 으로 찍는다.
    format: 'ratio',
    scopes: ['누계'],
  },
  {
    key: 'invoice',
    group: '수금',
    label: '세금계산서 발행',
    format: 'money',
    // 누계는 두지 않는다 — 발행은 그 달의 사건이고, 누적해서 볼 요구가 아직 없다(사용자 확정 2026-08-11).
    // (계산은 그대로 살아 있어 나중에 '누계' 를 이 배열에 되돌리면 바로 나온다.)
    scopes: ['당월'],
    input: 'invoiceAmount',
  },
  {
    key: 'collect',
    group: '수금',
    label: '실수금',
    format: 'money',
    scopes: ['당월', '누계'],
    input: 'collectAmount',
  },
  {
    key: 'receivable',
    group: '수금',
    label: '미수금 (발행−수금)',
    format: 'money',
    scopes: ['누계'],
    // 아직 개발 요구사항이 없어 감춰 둔다(사용자 확정 2026-08-11). 산식과 대시보드 집계는 살아 있다.
    hidden: true,
  },
  {
    key: 'collectRate',
    group: '수금',
    label: '수금률 (%)',
    format: 'percent',
    scopes: ['누계'],
  },
];

/** 표에 실제로 그리는 줄. 감춘 줄은 계산에만 남는다. */
export const VISIBLE_ROWS = ROWS.filter((row) => !row.hidden);

/** 칸에 <b>직접</b> 치는 줄 — 키보드 이동·붙여넣기의 좌표축이다. 투입금액도 들어온다(V580). */
export const INPUT_ROWS = VISIBLE_ROWS.filter((row) => row.input);

// ── 월 축 ───────────────────────────────────────────────────────────────────

const YM = /^\d{4}-(0[1-9]|1[0-2])$/;

export const isYm = (value: string) => YM.test(value);

export const ymOf = (date: string | null | undefined): string | null =>
  date && date.length >= 7 ? date.slice(0, 7) : null;

export function addMonths(ym: string, delta: number): string {
  const [year = 0, month = 1] = ym.split('-').map(Number);
  const total = year * 12 + (month - 1) + delta;
  return `${String(Math.floor(total / 12)).padStart(4, '0')}-${String((total % 12) + 1).padStart(2, '0')}`;
}

export const compareYm = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** 공사기간 앞뒤로 넓히는 달 수의 기본값 — 착공 전 준비와 준공 후 정산이 표 밖으로 나가지 않게 한다(사용자 확정). */
export const MONTH_PAD = 3;

/** 현장별 앞뒤 여유의 한도 (V616 · 사용자 지시 2026-11-02). */
export const MONTH_PAD_MAX = 12;

const padOf = (pad: number | null | undefined) =>
  pad == null || !Number.isFinite(pad) ? MONTH_PAD : Math.max(0, Math.min(MONTH_PAD_MAX, Math.trunc(pad)));

/**
 * 표의 월 열. <b>공사기간 시작 N개월 전 ~ 종료 M개월 후</b> — N·M 은 현장 설정(V616, 기본 3).
 *
 * <p>샘플 표가 2025-06 착공인데 2025-03 부터 시작하는 것이 기본 규칙이다.
 */
export function sheetMonths(
  settings: Pick<OfficeSettings, 'startDate' | 'endDate'> & Partial<Pick<OfficeSettings, 'monthPadBefore' | 'monthPadAfter'>>,
): string[] {
  const start = ymOf(settings.startDate);
  const end = ymOf(settings.endDate);
  if (!start || !end || !isYm(start) || !isYm(end) || compareYm(start, end) > 0) return [];
  const from = addMonths(start, -padOf(settings.monthPadBefore));
  const to = addMonths(end, padOf(settings.monthPadAfter));
  const list: string[] = [];
  for (let ym = from; compareYm(ym, to) <= 0; ym = addMonths(ym, 1)) {
    list.push(ym);
    if (list.length > 600) break;
  }
  return list;
}

export function yearsOf(months: string[]): number[] {
  return [...new Set(months.map((ym) => Number(ym.slice(0, 4))))].sort((a, b) => a - b);
}

export const monthLabel = (ym: string) => `${Number(ym.slice(5, 7))}월`;

export function thisMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * 마감 기한 — <b>익월 10일</b>(사용자 확정 2026-08-11).
 *
 * <p>정책은 바뀐다. 그래서 숫자를 코드 여기저기 흩지 않고 이 한 곳에 둔다 — 서버가 서면
 * `app_settings` 의 `office.closing_day` 로 옮겨 온다(V41 이 이미 키·값 표를 들고 있다).
 */
export const CLOSING_DAY = 10;

/** 그 달의 마감 기한(YYYY-MM-DD). */
export function closingDeadline(ym: string, closingDay = CLOSING_DAY): string {
  return `${addMonths(ym, 1)}-${String(closingDay).padStart(2, '0')}`;
}

/** 기한이 지났는데 아직 마감되지 않았는가 — 대시보드가 이 현장을 세워야 하는 이유다. */
export function isOverdue(draft: OfficeDraft, ym: string, todayDate: string, closingDay = CLOSING_DAY): boolean {
  if ((draft.months[ym] ?? EMPTY_MONTH).status === 'CONFIRMED') return false;
  if (!isInsideContract(draft.settings, ym)) return false;
  return todayDate > closingDeadline(ym, closingDay);
}

/** 공사기간 안인 달인지 — 앞뒤 여유 3개월은 흐리게 그린다. */
export function isInsideContract(settings: OfficeSettings, ym: string): boolean {
  const start = ymOf(settings.startDate);
  const end = ymOf(settings.endDate);
  if (!start || !end) return true;
  return compareYm(ym, start) >= 0 && compareYm(ym, end) <= 0;
}

// ── 금액·비율 표기 ──────────────────────────────────────────────────────────

export type AmountUnit = 1 | 1_000_000 | 100_000_000;

export const UNIT_LABEL: Record<AmountUnit, string> = {
  1: '원',
  1_000_000: '백만원',
  100_000_000: '억원',
};

/**
 * 고를 수 있는 눈금 — <b>억원 · 백만원 · 원</b>(사용자 지시 2026-09-14).
 *
 * <p>차례는 <b>큰 것부터</b>다. 이 회사의 금액은 대개 억 단위라 가장 자주 고르는 것이 왼쪽에
 * 서야 한다.
 *
 * <p>★ <b>원(1)이 목록에 있는 이유</b>는 자릿수를 보려는 것이 아니라 <b>정밀도</b> 때문이다.
 *   억원 눈금은 소수 두 자리까지만 보여 100만원 아래가 화면에서 사라진다 — 계약서에 적힌 값을
 *   끝자리까지 넣어야 할 때 여기로 바꾼다({@link commitAmount} 의 머리말).
 */
export const AMOUNT_UNITS: AmountUnit[] = [100_000_000, 1_000_000, 1];

/**
 * 처음 여는 사람이 보는 눈금. 원가관리 탭·전체 대시보드가 오래 써 온 값이라 그대로 둔다.
 *
 * <p>눈금 자체는 <b>화면이 아니라 사람에게 붙는다</b> — 어디서 바꾸든 전부 따라온다
 * ({@code useAmountUnit}). 한동안 계약금액만 백만원으로 못박아 두었던 적이 있는데, 그것은 눈금을
 * 하나로 만드는 대신 <b>토글이 안 듣는 칸</b>을 만드는 일이었다(2026-09-14 되돌림).
 */
export const DEFAULT_AMOUNT_UNIT: AmountUnit = 100_000_000;

/**
 * 사람이 친 것을 원(KRW) 정수로. `12.5`(억원 단위) · `1,250,000,000` · `824억` · `3천만` 을 받는다.
 * 빈칸은 `null`(미입력), 못 읽으면 `undefined` 를 돌려 <b>기존 값을 지키게</b> 한다.
 */
export function parseAmount(raw: string, unit: AmountUnit): number | null | undefined {
  const text = raw.replace(/[,\s원]/g, '').trim();
  if (!text) return null;

  const matched = /^(-?\d+(?:\.\d+)?)(억|천만|백만|만|천)?$/.exec(text);
  if (!matched) return undefined;
  const value = Number(matched[1]);
  if (!Number.isFinite(value)) return undefined;

  const factor: Record<string, number> = { 억: 100_000_000, 천만: 10_000_000, 백만: 1_000_000, 만: 10_000, 천: 1_000 };
  // 단위를 직접 쳤으면 그게 우선이다 — 화면 단위와 또 곱하면 0 이 여러 개 는다.
  const scale = matched[2] ? factor[matched[2]] ?? unit : unit;
  return Math.round(value * scale);
}

/** 퍼센트 입력. 0~100 을 넘어도 막지 않는다(설계변경 중에는 100 을 넘길 수 있다) — 색으로만 알린다. */
export function parseRate(raw: string): number | null | undefined {
  const text = raw.replace(/[%\s,]/g, '').trim();
  if (!text) return null;
  const value = Number(text);
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : undefined;
}

export function displayAmount(value: number | null | undefined, unit: AmountUnit): string {
  if (value == null) return '';
  return (value / unit).toLocaleString('ko-KR', { maximumFractionDigits: unit === 1 ? 0 : 2 });
}

/**
 * 금액 칸이 손을 뗄 때 <b>정말로 보낼 값</b>. 보낼 것이 없으면 {@code undefined} 다.
 *
 * <h2>왜 「같으면 안 보낸다」가 필요한가</h2>
 * 칸이 보여 주는 것은 {@link displayAmount} 가 <b>소수 두 자리로 줄인</b> 글자다. 그것을 그대로
 * 다시 파싱하면 눈금 아래 자리가 사라진다 — 억원 눈금에서 `108,371,980,000` 원은 `1,083.72` 로
 * 보이고, 그 칸을 스치듯 고쳤다가 되돌리면 `108,372,000,000` 원으로 <b>3억 2천만원이 조용히
 * 붙는다</b>(사용자 지적 2026-09-14: 표와 개요의 금액이 다르다).
 *
 * <p>그래서 <b>표시형이 그대로면 아무 일도 없었던 것</b>으로 본다. 사람이 실제로 숫자를 바꾼
 * 칸만 커밋되고, 그때 값은 사람이 친 그대로다 — 어느 쪽이든 절단이 일어나지 않는다.
 *
 * @param current 지금 들고 있는 값. 이것과 표시형이 같으면 보내지 않는다
 * @returns 보낼 값, 또는 {@code undefined}(못 읽었거나 바뀐 것이 없다 — 부르는 쪽은 그대로 둔다)
 */
export function commitAmount(raw: string, unit: AmountUnit, current: number | null): number | null | undefined {
  const parsed = parseAmount(raw, unit);
  if (parsed === undefined) return undefined;
  return displayAmount(parsed, unit) === displayAmount(current, unit) ? undefined : parsed;
}

export function displayRate(value: number | null | undefined): string {
  return value == null ? '' : value.toLocaleString('ko-KR', { maximumFractionDigits: 2 });
}

export const showAmount = (value: number | null, unit: AmountUnit): string =>
  value == null ? '—' : displayAmount(value, unit);

export const showRate = (value: number | null): string => (value == null ? '—' : `${displayRate(value)}%`);

/**
 * 퍼센트포인트를 <b>소수</b>로 — `10` → `0.10`.
 *
 * <p>원가율 비교만 이 표기를 쓴다. 같은 줄에 %가 둘(목표원가율·실적원가율) 있는데 그 <b>차이</b>까지
 * % 로 찍으면 셋이 같은 종류로 읽힌다. 차이는 비율이 아니라 <b>벌어진 정도</b>다(사용자 확정 2026-08-11).
 */
export const showRatio = (value: number | null): string =>
  value == null ? '—' : (value / 100).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 요약 카드 — 억원으로 접는다. */
export const summaryAmount = (value: number | null): string =>
  value == null ? '—' : `${(value / 100_000_000).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}억`;

// ── 계약금액 ────────────────────────────────────────────────────────────────

const sum = (values: (number | null)[]): number | null => {
  const found = values.filter((value): value is number => value != null);
  return found.length === 0 ? null : found.reduce((acc, value) => acc + value, 0);
};

/**
 * 「[ 건축공사 소계 ]」·「직접공사비 계」 같은 <b>합계 줄</b>인가.
 *
 * <p>공정표에서 공종을 지어 올 때 이런 줄이 공종으로 들어와, 형제들의 합이 한 번 더 더해졌다
 * (청주 도급액 2,726.5억 → 실제 1,152.6억, 2.37배). 줄은 지우지 않고 <b>합에서만</b> 뺀다.
 *
 * <p>서버 {@code WbsService.AGGREGATE_NAME} 과 <b>같은 식</b>이다 — 한쪽만 고치면 화면과
 * 내보내기·대시보드가 다른 도급액을 말한다.
 */
// JS 의 \s 는 전각 공백(U+3000)까지 받는다 — Java 쪽 전각 공백 칸과 같다.
const AGGREGATE_NAME = /[소합총누]\s*계|(?:^|[\s\])])계\s*[\])]?\s*$|\b(?:sub)?\s*total\b/i;

export const isAggregateTradeName = (name: string | null | undefined): boolean =>
  name != null && AGGREGATE_NAME.test(name);

/** 합계에 넣는 공종 줄 — 합계 줄(소계·계)을 뺀 것. 표에는 그대로 보인다 */
export const countableTrades = <T extends { trade: string }>(trades: T[]): T[] =>
  trades.filter((trade) => !isAggregateTradeName(trade.trade));

export const contractWithVat = (settings: OfficeSettings): number | null =>
  sum(countableTrades(settings.trades).map((trade) => trade.withVat));

export const contractWithoutVat = (settings: OfficeSettings): number | null =>
  sum(countableTrades(settings.trades).map((trade) => trade.withoutVat));

/** 수금비율 셋의 합. 100 이 아니면 설정 카드가 경고한다. */
export const collectRateSum = (settings: OfficeSettings): number =>
  (settings.advanceRate ?? 0) + (settings.progressRate ?? 0) + (settings.retentionRate ?? 0);

// ── 계산 (샘플 산식) ────────────────────────────────────────────────────────

export type RowSeries = { cur: (number | null)[]; cum: (number | null)[] };
export type Series = Record<RowKey, RowSeries>;

const div = (a: number | null, b: number | null): number | null =>
  a == null || b == null || b === 0 ? null : (a / b) * 100;

const pct = (rate: number | null, base: number | null): number | null =>
  rate == null || base == null ? null : Math.round((rate / 100) * base);

/**
 * 표 전체를 한 번에 계산한다.
 *
 * <p><b>누계 매출은 월별 매출의 합이 아니라 누계 공정률 × 계약금액</b>이다. 합으로 내면 반올림이
 * 달마다 쌓여 준공 시점에 계약금액과 안 맞는다 — 공정률이 원본이고 매출은 그 함수다.
 */
export function buildSeries(draft: OfficeDraft, months: string[]): Series {
  const { settings } = draft;
  const contractEx = contractWithoutVat(settings);
  const contractInc = contractWithVat(settings);
  const targetRate = settings.targetCostRate;
  // 본사관리비 요율(V167). 비어 있으면 그 줄은 통째로 미입력이고 총원가는 투입금액과 같아진다 —
  // 요율을 넣지 않은 현장의 숫자가 이 변경으로 달라지지 않는다.
  const overheadRate = settings.overheadRate;

  const empty = (): RowSeries => ({ cur: months.map(() => null), cum: months.map(() => null) });
  const series = Object.fromEntries(ROWS.map((row) => [row.key, empty()])) as Series;

  // 개시 잔액 — 진행 중인 현장을 옮겨 담을 때의 출발점.
  let planProgressCum = settings.openingPlanProgress ?? 0;
  let actualProgressCum = settings.openingActualProgress ?? 0;
  let costCum = settings.openingCost ?? 0;
  // 본사관리비에는 개시 잔액이 없다 — V167 이전에 굴러간 달에는 이 값 자체가 없었으므로 이월할 것도 없다.
  let overheadCum = 0;
  let anyOverhead = false;
  let invoiceCum = settings.openingInvoice ?? 0;
  let collectCum = settings.openingCollect ?? 0;
  let anyPlan = settings.openingPlanProgress != null;
  let anyActual = settings.openingActualProgress != null;
  let anyCost = settings.openingCost != null;
  let anyInvoice = settings.openingInvoice != null;
  let anyCollect = settings.openingCollect != null;

  /*
    비목 누계 (V180). <b>개시 잔액이 없다</b> — 이월 잔액(`openingCost`)은 비목이 없는 한 덩어리라
    셋 중 어디에도 넣을 수 없다. 넣으면 그 비목이 실제보다 커 보이고, 아무도 그 사실을 모른다.
    그래서 셋의 누계 합은 투입금액 누계보다 <b>이월액만큼 작다</b> — 그것이 맞다.
  */
  const kindCum: Record<CostKind, number> = { SUBCONTRACT: 0, MATERIAL: 0, EXPENSE: 0, ETC: 0 };
  const kindSeen: Record<CostKind, boolean> = {
    SUBCONTRACT: false, MATERIAL: false, EXPENSE: false, ETC: false,
  };

  months.forEach((ym, index) => {
    const input = draft.months[ym] ?? EMPTY_MONTH;

    const planCur = input.planProgressRate;
    const actualCur = input.actualProgressRate;
    const costCur = input.costAmount;
    const invoiceCur = input.invoiceAmount;
    const collectCur = input.collectAmount;

    if (planCur != null) {
      planProgressCum += planCur;
      anyPlan = true;
    }
    if (actualCur != null) {
      actualProgressCum += actualCur;
      anyActual = true;
    }
    if (costCur != null) {
      costCum += costCur;
      anyCost = true;
    }
    if (invoiceCur != null) {
      invoiceCum += invoiceCur;
      anyInvoice = true;
    }
    if (collectCur != null) {
      collectCum += collectCur;
      anyCollect = true;
    }

    // 아무도 입력하지 않은 구간은 0 이 아니라 <b>미입력</b>이다. 0 으로 그리면 미입력 현장이 흑자로 보인다.
    const planCumV = anyPlan ? round2(planProgressCum) : null;
    const actualCumV = anyActual ? round2(actualProgressCum) : null;
    const costCumV = anyCost ? costCum : null;
    const invoiceCumV = anyInvoice ? invoiceCum : null;
    const collectCumV = anyCollect ? collectCum : null;

    const planRevCur = pct(planCur, contractEx);
    const planRevCum = pct(planCumV, contractEx);
    const actualRevCur = pct(actualCur, contractEx);
    const actualRevCum = pct(actualCumV, contractEx);

    const set = (key: RowKey, cur: number | null, cum: number | null) => {
      series[key].cur[index] = cur;
      series[key].cum[index] = cum;
    };

    set('planProgress', planCur, planCumV);
    set('actualProgress', actualCur, actualCumV);
    set('planRevenue', planRevCur, planRevCum);
    set('actualRevenue', actualRevCur, actualRevCum);
    set('achieveRate', div(actualRevCur, planRevCur), div(actualRevCum, planRevCum));

    // 본사관리비는 <b>투입금액이 정해진 뒤</b> 나온다 — 그 달 투입금액 × 요율, 직접 적은 달은 적은 값(V167).
    const overheadCur = overheadOf(input, costCur, overheadRate);
    if (overheadCur != null) {
      overheadCum += overheadCur;
      anyOverhead = true;
    }
    const overheadCumV = anyOverhead ? overheadCum : null;

    // 총원가 — 아래 비교·원가율이 전부 이 줄을 쓴다.
    const totalCur = plusOrNull(costCur, overheadCur);
    const totalCum = plusOrNull(costCumV, overheadCumV);

    const planCostCur = pct(targetRate, planRevCur);
    const planCostCum = pct(targetRate, planRevCum);
    set('planCost', planCostCur, planCostCum);
    set('costInput', costCur, costCumV);

    // 비목 소계 — 그 달 칸들을 비목으로 접는다(V180). 「미분류」는 줄을 세우지 않지만 누계는
    // 함께 굴린다(나중에 줄을 세우게 되면 그때 값이 이미 맞아 있어야 한다).
    const byKind = costByKind(input.costByTrade ?? {});
    for (const kind of COST_KINDS) {
      const value = byKind[kind.key];
      if (value != null) {
        kindCum[kind.key] += value;
        kindSeen[kind.key] = true;
      }
    }
    set('costSubcontract', byKind.SUBCONTRACT, kindSeen.SUBCONTRACT ? kindCum.SUBCONTRACT : null);
    set('costMaterial', byKind.MATERIAL, kindSeen.MATERIAL ? kindCum.MATERIAL : null);
    set('costExpense', byKind.EXPENSE, kindSeen.EXPENSE ? kindCum.EXPENSE : null);

    set('overhead', overheadCur, overheadCumV);
    set('costTotal', totalCur, totalCum);
    // 비교 줄은 <b>양쪽이 다 있어야</b> 말한다 — 원가를 안 넣은 달에 목표금액·매출이 통째로 남는 돈으로 찍혔다.
    set('costDiffPlan', diffOrNull(planCostCur, totalCur), diffOrNull(planCostCum, totalCum));
    // 매출 쪽만 다르다(사용자 결정 2026-11-02) — 원가는 들었는데 매출이 없는 달은 그 원가만큼 손해다.
    // 원가가 비면 여전히 미입력이다.
    set('costDiffContract', lossOrNull(actualRevCur, totalCur), lossOrNull(actualRevCum, totalCum));

    set('targetCostRate', targetRate, targetRate);
    set('actualCostRate', div(totalCur, actualRevCur), div(totalCum, actualRevCum));
    set('costRateDiff', null, diffOrNull(targetRate, div(totalCum, actualRevCum)));

    set('invoice', invoiceCur, invoiceCumV);
    set('collect', collectCur, collectCumV);
    set('receivable', null, minusOrNull(invoiceCumV, collectCumV));
    set('collectRate', null, div(collectCumV, contractInc));
  });

  return series;
}

/** 둘 다 미입력이면 뺄셈도 미입력이다 — 0 을 만들어 내지 않는다. */
function minusOrNull(a: number | null, b: number | null): number | null {
  return a == null && b == null ? null : (a ?? 0) - (b ?? 0);
}

/**
 * 확정·마감 시각을 사람이 읽는 꼴로 — 「2026-08-13 15:49」. 서버는 ISO(「…T06:49:41.562901Z」)를 주고,
 * 그대로 찍으면 화면에 날것이 섰다. 날짜만 온 값(「2025-06-01」)은 그대로 둔다.
 */
export function stampLabel(value: string | null | undefined): string {
  if (!value) return '';
  if (!value.includes('T')) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const two = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())} ${two(date.getHours())}:${two(date.getMinutes())}`;
}

/** 비교 — 한쪽이라도 미입력이면 미입력이다. 빈 쪽을 0 으로 빼면 안 넣은 원가가 남는 돈으로 보인다. */
function diffOrNull(a: number | null, b: number | null): number | null {
  return a == null || b == null ? null : a - b;
}

/** 매출 − 원가. 원가가 없으면 미입력, 매출만 없으면 0 으로 보고 뺀다(그 달은 원가만큼 손해다). */
function lossOrNull(revenue: number | null, cost: number | null): number | null {
  return cost == null ? null : (revenue ?? 0) - cost;
}

/** 덧셈도 같다 — 둘 다 미입력이면 합도 미입력이다(총원가). */
function plusOrNull(a: number | null, b: number | null): number | null {
  return a == null && b == null ? null : (a ?? 0) + (b ?? 0);
}

/**
 * 그 달의 본사관리비(V167 · 요청 #35).
 *
 * <p>직접 적은 달은 적은 값이 이기고, 그렇지 않으면 <b>그 달 투입금액 × 요율</b>이다. 요율이나
 * 투입금액 어느 하나가 없으면 <b>미입력</b>이다 — 0 원으로 채우면 아직 안 적은 달이
 * 「관리비가 안 든 달」로 보인다.
 *
 * <p>서버에도 같은 식이 둘 있다(`OfficeSeries.overheadOf` · 대시보드 SQL 의 `overhead`).
 * 고칠 일이 생기면 <b>셋을 같이</b> 고친다 — 하나만 고치면 같은 현장이 세 자리에서 다른 원가를 말한다.
 */
function overheadOf(input: MonthInput, costCur: number | null, overheadRate: number | null): number | null {
  if (input.overheadSource === 'MANUAL') return input.overheadAmount;
  return pct(overheadRate, costCur);
}

const round2 = (value: number) => Math.round(value * 100) / 100;

// ── 보할 공정표 들여오기 ────────────────────────────────────────────────────

/**
 * 계획 곡선(누계 %)을 목표공정률로 바꿔 넣는다.
 *
 * <p>받는 것은 <b>누계</b>다 — 공정표가 그리는 것이 누계 곡선이고, `pmis_wbs_planned_curve.planned_rate`
 * 도 누계다. 표에 넣을 때 앞 달과의 차이로 당월값을 만든다. 반대로(당월을 받아 누계를 만들면)
 * 마지막 달이 100% 에 안 떨어지는 일이 생긴다.
 *
 * @param rows `[ym, 누계 %]` — 달이 비어 있으면 그 사이는 앞 값을 이어 간다
 */
export function planCurveToMonthly(rows: [string, number][]): Record<string, number> {
  const sorted = [...rows].filter(([ym]) => isYm(ym)).sort((a, b) => compareYm(a[0], b[0]));
  const monthly: Record<string, number> = {};
  let previous = 0;
  for (const [ym, cumulative] of sorted) {
    monthly[ym] = Math.round((cumulative - previous) * 100) / 100;
    previous = cumulative;
  }
  return monthly;
}

/**
 * 붙여넣은 글을 `[ym, 누계%]` 로 읽는다. 두 가지 모양을 받는다.
 * <ul>
 *   <li>{@code 2025-07\t12.5} — 달과 값이 한 줄에</li>
 *   <li>{@code 12.5} 한 줄에 값만 — 이때는 `firstYm` 부터 차례로 붙인다</li>
 * </ul>
 */
export function parsePlanCurve(text: string, firstYm: string): [string, number][] {
  const rows: [string, number][] = [];
  let cursor = firstYm;
  for (const line of text.replace(/\r/g, '').split('\n')) {
    if (!line.trim()) continue;
    const parts = line.split(/[\t,]/).map((part) => part.trim());
    const hasYm = parts[0] != null && isYm(normalizeYm(parts[0]));
    const ym = hasYm ? normalizeYm(parts[0]!) : cursor;
    const raw = hasYm ? parts[1] : parts[0];
    const value = Number((raw ?? '').replace(/[%\s,]/g, ''));
    if (!Number.isFinite(value)) continue;
    rows.push([ym, value]);
    cursor = addMonths(ym, 1);
  }
  return rows;
}

/** `2025.07` · `2025/7` 처럼 쳐도 받아 준다. */
function normalizeYm(raw: string): string {
  const matched = /^(\d{4})[-./](\d{1,2})$/.exec(raw.trim());
  if (!matched) return raw.trim();
  return `${matched[1]}-${String(Number(matched[2])).padStart(2, '0')}`;
}

/** 투입금액의 합(공종·비목 전부). 하나도 없으면 미입력(null)이다 — 0 원과 다르다. */
export function costTotalOf(byTrade: Record<string, number | null>): number | null {
  const values = Object.values(byTrade).filter((value): value is number => value != null);
  return values.length === 0 ? null : values.reduce((acc, value) => acc + value, 0);
}

/**
 * 비목 (V180 · 요청 No.35).
 *
 * <p>순서가 화면 순서다 — 요청서 문장이 「외주 + 자재 + 경비」이고, 금액이 큰 순서이기도 하다.
 * 「미분류」가 마지막인 것은 <b>고쳐야 할 것</b>이기 때문이다: V180 이전 자료가 전부 여기 있다.
 */
export const COST_KINDS = [
  { key: 'SUBCONTRACT', label: '외주' },
  { key: 'MATERIAL', label: '자재' },
  { key: 'EXPENSE', label: '경비' },
  { key: 'ETC', label: '미분류' },
] as const;

export type CostKind = (typeof COST_KINDS)[number]['key'];

/**
 * 「공종 × 비목」 한 칸의 키.
 *
 * <p>비목을 <b>앞</b>에 둔다. 비목은 정해진 넷이라 첫 구분자에서 자르면 공종에 무엇이 들어 있든
 * 안전하게 갈린다 — 공종은 사람이 치는 이름이라 어떤 글자든 들어올 수 있다.
 * 구분자는 이름에 절대 안 들어가는 제어문자(U+001F)다.
 */
export function costCellKey(trade: string, kind: CostKind): string {
  return `${kind}${trade}`;
}

/** 키를 되돌린다. 옛 키(비목 없이 공종만)는 <b>미분류</b>로 읽는다 — V180 이전 화면이 만든 것이다. */
export function parseCostCellKey(key: string): { trade: string; kind: CostKind } {
  const at = key.indexOf('');
  if (at < 0) return { trade: key, kind: 'ETC' };
  const kind = key.slice(0, at) as CostKind;
  return { trade: key.slice(at + 1), kind: COST_KINDS.some((k) => k.key === kind) ? kind : 'ETC' };
}

/** 비목별 소계. 표의 「외주·자재·경비」 줄이 이걸로 선다. */
export function costByKind(byTrade: Record<string, number | null>): Record<CostKind, number | null> {
  const sums: Record<string, number | null> = {};
  for (const [key, amount] of Object.entries(byTrade)) {
    if (amount == null) continue;
    const { kind } = parseCostCellKey(key);
    sums[kind] = (sums[kind] ?? 0) + amount;
  }
  return {
    SUBCONTRACT: sums.SUBCONTRACT ?? null,
    MATERIAL: sums.MATERIAL ?? null,
    EXPENSE: sums.EXPENSE ?? null,
    ETC: sums.ETC ?? null,
  };
}

// ── 잠금 ────────────────────────────────────────────────────────────────────

/**
 * 이 달을 지금 고칠 수 있는가.
 *
 * <p>두 가지가 모두 참이어야 열린다.
 * <ol>
 *   <li>그 달이 마감되지 않았다</li>
 *   <li><b>더 뒤의 달이 마감되지 않았다</b> — 이번 달을 확정하면 지난달은 누구도 못 고친다(사용자 확정)</li>
 * </ol>
 *
 * <p>설정 확정은 <b>입력을 막지 않는다</b>(2026-10-27) — 확정 전에 칸이 전부 잠겨 있어 아무도 값을 넣지
 * 않았다. 확정이 필요한 것은 월 마감이고, 그 판정은 {@link monthBlockers} 와 서버가 한다.
 */
export function isMonthLocked(draft: OfficeDraft, ym: string): boolean {
  if ((draft.months[ym] ?? EMPTY_MONTH).status === 'CONFIRMED') return true;
  return Object.entries(draft.months).some(
    ([key, month]) => month.status === 'CONFIRMED' && compareYm(key, ym) > 0,
  );
}

export function lastConfirmedYm(draft: OfficeDraft): string | null {
  const confirmed = Object.entries(draft.months)
    .filter(([, month]) => month.status === 'CONFIRMED')
    .map(([ym]) => ym)
    .sort();
  return confirmed[confirmed.length - 1] ?? null;
}

/**
 * 이 달을 마감해도 되는가 — <b>세금계산서를 발행했다면 PDF 가 있어야 한다</b>(사용자 확정).
 * 돈이 들어온 것으로 적히는 줄에 증빙이 없으면 나중에 아무도 그 숫자를 지킬 수 없다.
 */
export function monthBlockers(draft: OfficeDraft, ym: string): string[] {
  const month = draft.months[ym] ?? EMPTY_MONTH;
  const blockers: string[] = [];
  if (draft.settings.status !== 'CONFIRMED') blockers.push('현장 설정이 아직 확정되지 않았습니다.');
  if (month.invoiceAmount != null && month.invoiceAmount !== 0 && !month.invoiceFileName)
    blockers.push('세금계산서 PDF 가 첨부되지 않았습니다.');
  if (month.actualProgressRate == null) blockers.push('실적공정률이 비어 있습니다.');
  return blockers;
}

/** 설정 확정을 막는 것들 — 확정하면 매출 산식이 이 값들 위에서 돌기 시작한다. */
export function settingsBlockers(settings: OfficeSettings): string[] {
  const blockers: string[] = [];
  if (!settings.startDate || !settings.endDate) blockers.push('공사기간을 넣어 주세요.');
  if (contractWithoutVat(settings) == null) blockers.push('도급계약금액(VAT제외)이 비어 있습니다.');
  if (settings.targetCostRate == null) blockers.push('목표원가율이 비어 있습니다.');
  const rateSum = collectRateSum(settings);
  if (Math.abs(rateSum - 100) > 0.01) blockers.push(`수금비율 합이 ${rateSum}% 입니다 (100% 이어야 합니다).`);
  return blockers;
}

// ── 서버 응답 → 표가 쓰는 모양 ───────────────────────────────────────────────

/**
 * 서버가 준 시트를 화면 모형으로 접는다.
 *
 * <p>산식·잠금 규칙은 이 파일 위쪽 그대로다 — 저장 자리가 브라우저에서 서버로 옮겨 갔을 뿐,
 * <b>무엇을 어떻게 계산하는가는 바뀌지 않았다</b>(테스트도 그대로 산다).
 *
 * <p>서버는 표를 정규화해 내려준다(월·공종내역·증빙이 따로). 화면은 한 달을 한 덩어리로 다루므로
 * 여기서 다시 붙인다 — 붙이는 자리를 화면 곳곳에 흩으면 같은 조인이 여러 벌 생긴다.
 */
export function sheetToDraft(sheet: OfficeSheetResponse, site?: Site | null): OfficeDraft {
  const settings = sheet.settings;

  const months: Record<string, MonthInput> = {};
  for (const row of sheet.months) {
    months[row.periodYm] = {
      planProgressRate: row.planProgressRate,
      actualProgressRate: row.actualProgressRate,
      actualSource: row.actualSource ?? 'LEDGER',
      costAmount: row.costAmount,
      costSource: row.costSource ?? 'DETAIL',
      costByTrade: {},
      overheadAmount: row.overheadAmount,
      overheadSource: row.overheadSource ?? 'RATE',
      invoiceAmount: row.invoiceAmount,
      collectAmount: row.collectAmount,
      invoiceSource: row.invoiceSource ?? 'LEDGER',
      collectSource: row.collectSource ?? 'LEDGER',
      invoiceFileName: null,
      note: row.note ?? '',
      status: row.status,
      confirmedAt: row.confirmedAt,
    };
  }
  const ensure = (ym: string): MonthInput => {
    months[ym] ??= { ...EMPTY_MONTH, costByTrade: {} };
    return months[ym]!;
  };
  for (const detail of sheet.costDetails) {
    // 서버가 비목을 안 준 옛 응답이면 「미분류」로 받는다 — 값을 잃는 것보다 낫다.
    const kind = (detail.costKind ?? 'ETC') as CostKind;
    ensure(detail.periodYm).costByTrade[costCellKey(detail.trade ?? '', kind)] = detail.amount;
  }
  /*
    증빙은 한 달에 여러 장일 수 있다. 표에는 '붙어 있다'만 보이면 되므로 첫 장의 이름을 쓴다.
    수금관리 회차에 붙인 파일도 여기 섞여 온다(2026-09-15) — 같은 서류를 두 번 올리지 않는다.
    달이 없는 줄은 건너뛴다: `ensure` 가 빈 달을 만들어 표에 없던 열이 서는 것을 막는다.
  */
  for (const invoice of sheet.invoices) {
    if (!invoice.periodYm) continue;
    const month = ensure(invoice.periodYm);
    month.invoiceFileName ??= invoice.fileName;
  }

  return {
    progressWeeks: sheet.progressWeeks,
    settings: {
      startDate: settings?.startDate ?? site?.actualStartDate ?? site?.contractStartDate ?? null,
      endDate: settings?.endDate ?? site?.scheduledCompletionDate ?? site?.contractEndDate ?? null,
      targetCompletionDate: settings?.targetCompletionDate ?? site?.targetCompletionDate ?? null,
      monthPadBefore: settings?.monthPadBefore ?? MONTH_PAD,
      monthPadAfter: settings?.monthPadAfter ?? MONTH_PAD,
      // 설정이 아직 없으면 기본 공종 넷을 세워 둔다 — 빈 표를 주면 어디부터 채울지 알 수 없다.
      trades: sheet.trades.length > 0
        ? sheet.trades.map((trade) => ({
            id: trade.id,
            trade: trade.trade,
            withVat: trade.amountWithVat,
            withoutVat: trade.amountWithoutVat,
          }))
        : DEFAULT_TRADES.map((trade) => ({ trade, withVat: null, withoutVat: null })),
      targetCostRate: settings?.targetCostRate ?? null,
      overheadRate: settings?.overheadRate ?? null,
      advanceRate: settings?.advanceRate ?? null,
      progressRate: settings?.progressRate ?? null,
      retentionRate: settings?.retentionRate ?? null,
      claimCycleMonths: settings?.claimCycleMonths ?? null,
      collectTerm: settings?.collectTerm ?? '',
      collectBase: settings?.collectBase ?? null,
      collectOffsetDays: settings?.collectOffsetDays ?? null,
      openingPlanProgress: settings?.openingPlanProgress ?? null,
      openingActualProgress: settings?.openingActualProgress ?? null,
      openingCost: settings?.openingCost ?? null,
      openingInvoice: settings?.openingInvoice ?? null,
      openingCollect: settings?.openingCollect ?? null,
      status: settings?.status ?? 'DRAFT',
      confirmedAt: settings?.confirmedAt ?? null,
      contractLockedAt: settings?.contractLockedAt ?? null,
      changes: sheet.changeOrders.map((order) => ({
        at: (order.openedAt ?? '').slice(0, 10),
        reason: order.reason,
        fileName: order.fileName,
        beforeWithoutVat: order.beforeWithoutVat,
        beforeWithVat: order.beforeWithVat,
      })),
      /** 설정이 서버에 아직 없으면 저장 경로가 '만들기'다. 화면이 안내 문구를 가른다. */
      persisted: settings != null,
      updatedAt: settings?.updatedAt ?? null,
    },
    months,
  };
}

/**
 * 설정 저장 본문. 화면 모형에서 서버가 아는 칸만 추린다.
 *
 * <p><b>본사관리비 요율은 여기 없다</b>(V167). 이 경로는 확정 전에만 열리는데 요율은 확정 뒤에도
 * 채워야 하고, 넣는 손도 다르다(본사) — 저장 경로가 따로다.
 */
export function toSettingsPayload(settings: OfficeSettings): OfficeSettingsPayload {
  return {
    startDate: settings.startDate,
    endDate: settings.endDate,
    targetCompletionDate: settings.targetCompletionDate,
    targetCostRate: settings.targetCostRate,
    advanceRate: settings.advanceRate,
    progressRate: settings.progressRate,
    retentionRate: settings.retentionRate,
    claimCycleMonths: settings.claimCycleMonths,
    collectTerm: settings.collectTerm,
    collectBase: settings.collectBase,
    collectOffsetDays: settings.collectOffsetDays,
    openingPlanProgress: settings.openingPlanProgress,
    openingActualProgress: settings.openingActualProgress,
    openingCost: settings.openingCost,
    openingInvoice: settings.openingInvoice,
    openingCollect: settings.openingCollect,
  };
}

export function toTradesPayload(settings: OfficeSettings): OfficeTradePayload[] {
  return settings.trades.map((trade) => ({
    id: trade.id ?? null,
    trade: trade.trade,
    amountWithVat: trade.withVat,
    amountWithoutVat: trade.withoutVat,
  }));
}
